import React, { useState, useEffect } from 'react';
import { ArrowLeft, Eye, Phone, Mail, MessageSquare, FileText, CreditCard } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Panel, Section, Button, Badge, LoadingSpinner, TranscriptDisplay, CallModal, SMSModal, EditableField, AddCardModal } from './components';

function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('appointments');
  const [transactions, setTransactions] = useState([]);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [editingContact, setEditingContact] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', relationship: '', phone: '' });

  useEffect(() => {
    if (id) {
      fetchClientDetails();
    }
  }, [id]);

  const fetchClientDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getClient(id);
      setClient(data);

      // Fetch appointments for this client
      const appointmentsData = await apiService.getAppointments({ client: id });
      setAppointments(appointmentsData.results || appointmentsData || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch client details');
      console.error('Error fetching client:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSave = async (overrideKey, value) => {
    const data = { [overrideKey]: value };
    const updatedClient = await apiService.updateClient(id, data);
    setClient(updatedClient);
  };

  const saveContact = async () => {
    try {
      const data = {
        contact_name_override: contactForm.name,
        contact_relationship: contactForm.relationship,
      };
      const updatedClient = await apiService.updateClient(id, data);
      setClient(updatedClient);
      setEditingContact(false);
    } catch (err) {
      alert('Failed to save contact: ' + (err.message || 'Unknown error'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toFixed(2)}`;
  };

  const getServiceType = (appointment) => {
    if (appointment.is_alf) return 'ALF';
    if (appointment.is_event) return 'Event';
    return 'Standard';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'call':
      case 'phone_call':
      case 'google_ads_phone_call':
      case 'google_ads_call':
        return <Phone className="w-4 h-4" />;
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'sms':
      case 'phone_text':
        return <MessageSquare className="w-4 h-4" />;
      case 'website_message':
      case 'google_ads_lead':
        return <FileText className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const isPhoneCall = (type) => {
    return ['call', 'phone_call', 'google_ads_phone_call', 'google_ads_call'].includes(type);
  };

  const getInteractionBadgeColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-blue-100 text-blue-800';
      case 'contacted':
        return 'bg-yellow-100 text-yellow-800';
      case 'converted':
        return 'bg-green-100 text-green-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Header with Back Button */}
        <div className="mb-3 flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/clients')}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1" />
            Back
          </Button>
          <h1 className="text-lg font-semibold text-gray-900 leading-none">Client Details</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        )}

        {/* Error State */}
        {error && !loading && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <h3 className="text-xs font-medium text-red-800">Error loading client</h3>
                <p className="mt-0.5 text-xs text-red-700">{error}</p>
                <button
                  onClick={fetchClientDetails}
                  className="mt-1 text-xs font-medium text-red-800 hover:text-red-900"
                >
                  Try again
                </button>
              </div>
            </div>
          </Panel>
        )}

        {/* Content */}
        {!loading && !error && client && (
          <div className="space-y-3">
            {/* Deceased Banner */}
            {client.followup_status === 'deceased' && (
              <div className="bg-gray-900 text-white px-4 py-3 rounded-lg flex items-center gap-2">
                <span className="text-lg">🕊</span>
                <div>
                  <p className="text-sm font-semibold">This client is marked as deceased</p>
                  <p className="text-xs text-gray-300">No appointments should be booked for this client.</p>
                </div>
              </div>
            )}
            {/* Client Information */}
            <Panel>
              <Section title={<div className="flex items-center gap-2">
                Client Information
                {client.followup_status === 'deceased' ? (
                  <button onClick={async () => {
                    if (window.confirm('Remove deceased status from this client?')) {
                      const updated = await apiService.updateClient(id, { followup_status: 'active' });
                      setClient(updated);
                    }
                  }} className="text-[10px] px-2 py-0.5 bg-gray-200 text-gray-600 rounded-full hover:bg-gray-300">
                    Remove Deceased
                  </button>
                ) : (
                  <button onClick={async () => {
                    if (window.confirm('Mark this client as deceased? This will prevent future bookings and follow-ups.')) {
                      const updated = await apiService.updateClient(id, { followup_status: 'deceased' });
                      setClient(updated);
                    }
                  }} className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full hover:bg-red-100 hover:text-red-700">
                    Mark Deceased
                  </button>
                )}
              </div>}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Client ID</p>
                    <p className="text-xs font-medium text-gray-900">{client.id}</p>
                  </div>
                  <EditableField
                    label="First Name"
                    effectiveValue={client.effective_first_name}
                    squareValue={client.person?.first_name}
                    overrideValue={client.override_first_name}
                    overrideKey="override_first_name"
                    onSave={handleFieldSave}
                  />
                  <EditableField
                    label="Last Name"
                    effectiveValue={client.effective_last_name}
                    squareValue={client.person?.last_name}
                    overrideValue={client.override_last_name}
                    overrideKey="override_last_name"
                    onSave={handleFieldSave}
                  />
                  <div>
                    <EditableField
                      label="Phone"
                      effectiveValue={client.effective_phone}
                      squareValue={client.person?.phone_number}
                      overrideValue={client.override_phone}
                      overrideKey="override_phone"
                      onSave={handleFieldSave}
                      inputType="tel"
                    />
                    {(client.effective_phone || client.person?.phone_number) && (
                      <div className="flex gap-1 mt-0.5">
                        <button
                          onClick={() => setShowCallModal(true)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                          title="Call this client"
                        >
                          <Phone className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => setShowSMSModal(true)}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                          title="Text this client"
                        >
                          <MessageSquare className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                  <EditableField
                    label="Email"
                    effectiveValue={client.effective_email}
                    squareValue={client.person?.email}
                    overrideValue={client.override_email}
                    overrideKey="override_email"
                    onSave={handleFieldSave}
                    inputType="email"
                  />
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Square ID</p>
                    <p className="text-xs font-medium text-gray-900">{client.square_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Location</p>
                    <p className="text-xs font-medium text-gray-900">{client.location?.name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Appointments</p>
                    <p className="text-xs font-medium text-gray-900">{client.appointment_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Total Spent</p>
                    <p className="text-xs font-semibold text-green-600">{formatCurrency(client.total_spent)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Last Appt</p>
                    <p className="text-xs font-medium text-gray-900">{client.last_appointment_date ? formatDate(client.last_appointment_date) : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Client Type</p>
                    <Badge variant="neutral" small>
                      {client.alf_client ? 'ALF' : client.event_client ? 'Event' : client.home_client ? 'Home' : 'N/A'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Card on File</p>
                    {client.has_card_on_file ? (
                      <div className="flex items-center gap-1.5">
                        <CreditCard className="w-3 h-3 text-green-600" />
                        <span className="text-xs font-medium text-green-600">{client.card_brand} ****{client.card_last_four}</span>
                        <button
                          onClick={() => setShowAddCard(true)}
                          className="text-[10px] text-gray-400 hover:text-blue-600 ml-1"
                          title="Replace card"
                        >Replace</button>
                        <button
                          onClick={async () => {
                            if (window.confirm('Remove this card from file? This will detach it from Stripe.')) {
                              try {
                                await apiService.post('/amn/payments/delete-card/', { client_id: id });
                                fetchClientDetails();
                              } catch (err) { alert('Failed to delete card: ' + err.message); }
                            }
                          }}
                          className="text-[10px] text-gray-400 hover:text-red-600"
                          title="Delete card"
                        >Delete</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowAddCard(true)}
                        className="flex items-center gap-1 px-2 py-0.5 text-xs bg-red-800 text-white rounded hover:bg-red-900 transition-colors"
                      >
                        <CreditCard className="w-3 h-3" />
                        Add Card
                      </button>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Contact Person</p>
                    {editingContact ? (
                      <div className="space-y-1.5 bg-purple-50 border border-purple-200 rounded p-2">
                        <input
                          type="text"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                          placeholder="Contact name"
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                        <select
                          value={contactForm.relationship}
                          onChange={(e) => setContactForm({ ...contactForm, relationship: e.target.value })}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                        >
                          <option value="">No contact person</option>
                          <option value="self">Self</option>
                          <option value="son">Son</option>
                          <option value="daughter">Daughter</option>
                          <option value="spouse">Spouse</option>
                          <option value="spouse (deceased)">Spouse (Deceased)</option>
                          <option value="caretaker">Caretaker</option>
                          <option value="facility_staff">Facility Staff</option>
                          <option value="other">Other</option>
                        </select>
                        <input
                          type="tel"
                          value={contactForm.phone}
                          onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                          placeholder="Contact phone (optional)"
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
                        />
                        <div className="flex gap-1">
                          <button onClick={saveContact} className="px-2 py-0.5 text-[10px] font-medium bg-purple-600 text-white rounded hover:bg-purple-700">Save</button>
                          <button onClick={() => setEditingContact(false)} className="px-2 py-0.5 text-[10px] font-medium text-gray-600 hover:text-gray-900">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 cursor-pointer group" onClick={() => {
                        setContactForm({
                          name: client.contact_name_override || '',
                          relationship: client.contact_relationship || '',
                          phone: client.contact_person?.phone_number || ''
                        });
                        setEditingContact(true);
                      }}>
                        {client.contact_relationship && client.contact_relationship !== 'self' ? (
                          <>
                            <span className="text-xs font-medium text-purple-700">{client.contact_name_override || 'Unknown'}</span>
                            <span className="text-[10px] px-1.5 py-0.5 bg-purple-50 text-purple-600 rounded">{client.contact_relationship}</span>
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 group-hover:text-purple-600">+ Add contact person</span>
                        )}
                        <span className="text-[10px] text-gray-300 group-hover:text-purple-400 ml-1">✎</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">SMS Notifications</p>
                    <button
                      onClick={async () => {
                        const updated = await apiService.updateClient(id, {
                          sms_notifications_enabled: !client.sms_notifications_enabled
                        });
                        setClient(updated);
                      }}
                      className={`text-xs font-medium px-2 py-0.5 rounded ${
                        client.sms_notifications_enabled !== false
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {client.sms_notifications_enabled !== false ? '✓ Enabled' : '✗ Disabled'}
                    </button>
                  </div>
                  <EditableField
                    label="Address"
                    effectiveValue={client.effective_address}
                    squareValue={client.person?.address}
                    overrideValue={client.override_address}
                    overrideKey="override_address"
                    onSave={handleFieldSave}
                  />
                  <EditableField
                    label="City"
                    effectiveValue={client.effective_city}
                    squareValue={client.person?.city}
                    overrideValue={client.override_city}
                    overrideKey="override_city"
                    onSave={handleFieldSave}
                  />
                  <EditableField
                    label="Zip Code"
                    effectiveValue={client.effective_zip_code}
                    squareValue={client.person?.zip_code}
                    overrideValue={client.override_zip_code}
                    overrideKey="override_zip_code"
                    onSave={handleFieldSave}
                  />
                  {/* AI Profile Fields */}
                  {client.profile?.situation?.age && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Age</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.situation.age}</p>
                    </div>
                  )}
                  {client.profile?.situation?.mobility && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Mobility</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.situation.mobility}</p>
                    </div>
                  )}
                  {client.profile?.situation?.living_arrangement && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Living</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.situation.living_arrangement}</p>
                    </div>
                  )}
                  {client.profile?.situation?.facility_name && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Facility</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.situation.facility_name}</p>
                    </div>
                  )}
                  {client.profile?.preferences?.favorite_technician && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Fav Team Member</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.preferences.favorite_technician}</p>
                    </div>
                  )}
                  {client.profile?.preferences?.scheduling_preference && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Scheduling</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.preferences.scheduling_preference}</p>
                    </div>
                  )}
                  {client.profile?.important_info?.contact_person && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Contact</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.important_info.contact_person}</p>
                    </div>
                  )}
                  {client.profile?.important_info?.access_codes?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Access Code</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.important_info.access_codes.join(', ')}</p>
                    </div>
                  )}
                  {client.profile?.important_info?.location_details && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Location Details</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.important_info.location_details}</p>
                    </div>
                  )}
                  {client.profile?.situation?.health_conditions?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Health</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.situation.health_conditions.join(', ')}</p>
                    </div>
                  )}
                  {client.profile?.important_info?.allergies?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Allergies</p>
                      <p className="text-xs font-medium text-red-600">{client.profile.important_info.allergies.join(', ')}</p>
                    </div>
                  )}
                  {client.profile?.preferences?.favorite_colors?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Colors</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.preferences.favorite_colors.join(', ')}</p>
                    </div>
                  )}
                  {client.profile?.preferences?.service_preferences?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Services</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.preferences.service_preferences.join(', ')}</p>
                    </div>
                  )}
                  {client.profile?.preferences?.special_instructions?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Instructions</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.preferences.special_instructions.join(', ')}</p>
                    </div>
                  )}
                  {client.profile?.important_info?.special_requests?.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Requests</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.important_info.special_requests.join(', ')}</p>
                    </div>
                  )}
                  {client.profile?.personality?.notes && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">AI Notes</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.personality.notes}</p>
                    </div>
                  )}
                  {client.profile?.important_info?.parking_instructions && (
                    <div>
                      <p className="text-xs text-gray-500 mb-0.5">Parking</p>
                      <p className="text-xs font-medium text-gray-900">{client.profile.important_info.parking_instructions}</p>
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <EditableField
                    label="Notes"
                    effectiveValue={client.notes}
                    squareValue={null}
                    overrideValue={client.notes}
                    overrideKey="notes"
                    onSave={handleFieldSave}
                    inputType="textarea"
                  />
                </div>
              </Section>
            </Panel>

            {/* Tabs Navigation */}
            <Panel noPadding>
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px" aria-label="Tabs">
                  <button
                    onClick={() => setActiveTab('appointments')}
                    className={`flex-1 py-2.5 px-3 text-center text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'appointments'
                        ? 'border-red-800 text-red-800'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Appointments ({appointments.length})
                  </button>
                  <button
                    onClick={() => setActiveTab('interactions')}
                    className={`flex-1 py-2.5 px-3 text-center text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'interactions'
                        ? 'border-red-800 text-red-800'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Interactions ({client.interactions?.length || 0})
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('photos');
                      if (photos.length === 0) {
                        apiService.get('/amn/appointment-pictures/', { appointment__client: id, page_size: 200 })
                          .then(data => setPhotos(data.results || data || []))
                          .catch(() => {});
                      }
                    }}
                    className={`flex-1 py-2.5 px-3 text-center text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'photos'
                        ? 'border-red-800 text-red-800'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Photos
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab('transactions');
                      if (transactions.length === 0) {
                        apiService.get('/amn/transactions/', { client: id, ordering: '-date_time', page_size: 100 })
                          .then(data => setTransactions(data.results || data || []))
                          .catch(() => {});
                      }
                    }}
                    className={`flex-1 py-2.5 px-3 text-center text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'transactions'
                        ? 'border-red-800 text-red-800'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Transactions
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-[300px]">
                {activeTab === 'appointments' && (
                  <>
                    {appointments.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-8">No appointments found for this client</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Date</th>
                              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Team Member</th>
                              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Type</th>
                              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Exp Rev</th>
                              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Paid</th>
                              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Status</th>
                              <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Details</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {appointments
                              .sort((a, b) => new Date(b.date_time) - new Date(a.date_time))
                              .map((appointment) => {
                                const isCancelled = appointment.status?.toLowerCase().includes('cancel');
                                return (
                                <tr key={appointment.id} className={isCancelled ? 'bg-red-50/50 opacity-60' : 'hover:bg-gray-50'}>
                                  <td className={`px-2 py-1.5 text-xs ${isCancelled ? 'line-through text-gray-400' : 'text-gray-900'}`}>{formatDate(appointment.date_time)}</td>
                                  <td className={`px-2 py-1.5 text-xs ${isCancelled ? 'text-gray-400' : 'text-gray-900'}`}>{appointment.technician_name || 'N/A'}</td>
                                  <td className={`px-2 py-1.5 text-xs ${isCancelled ? 'text-gray-400' : 'text-gray-900'}`}>{getServiceType(appointment)}</td>
                                  <td className={`px-2 py-1.5 text-xs text-right ${isCancelled ? 'text-gray-400' : 'text-gray-900'}`}>{isCancelled ? '-' : formatCurrency(appointment.expected_revenue)}</td>
                                  <td className={`px-2 py-1.5 text-xs text-right ${isCancelled ? 'text-gray-400' : 'text-gray-900'}`}>{isCancelled ? '-' : formatCurrency(appointment.total_payment)}</td>
                                  <td className="px-2 py-1.5 text-xs text-center">
                                    {isCancelled ? (
                                      <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700">Cancelled</span>
                                    ) : appointment.is_paid ? (
                                      <span className="text-green-600">✓</span>
                                    ) : (
                                      <span className="text-red-600">✗</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1.5 text-xs text-center">
                                    <button
                                      onClick={() => navigate(`/appointments/${appointment.id}`)}
                                      className="text-gray-400 hover:text-red-800 transition-colors"
                                      title="View Details"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                  </td>
                                </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'interactions' && (
                  <>
                    {!client.interactions || client.interactions.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-8">No interactions found for this client</p>
                    ) : (
                      <div className="divide-y divide-gray-200">
                        {client.interactions
                          .sort((a, b) => new Date(b.date_time) - new Date(a.date_time))
                          .map((interaction) => (
                            <div key={interaction.id} className="px-3 py-2 hover:bg-gray-50">
                              <div className="flex items-start gap-2">
                                <div className="flex-shrink-0 mt-0.5 text-gray-400">
                                  {getInteractionIcon(interaction.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-medium text-gray-900">
                                      {interaction.type_display}
                                    </span>
                                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${getInteractionBadgeColor(interaction.status)}`}>
                                      {interaction.status_display}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      {formatDateTime(interaction.date_time)}
                                    </span>
                                  </div>
                                  {interaction.content && (
                                    <p className="text-xs text-gray-600 line-clamp-2">
                                      {interaction.content}
                                    </p>
                                  )}
                                  {interaction.file_url && (
                                    <a
                                      href={interaction.file_url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-red-600 hover:text-red-800 mt-1 inline-block"
                                    >
                                      View Recording
                                    </a>
                                  )}
                                  {interaction.transcript ? (
                                    <div className="mt-2">
                                      <TranscriptDisplay
                                        transcript={interaction.transcript}
                                        maxHeight={150}
                                      />
                                    </div>
                                  ) : isPhoneCall(interaction.type) && (
                                    <p className="text-xs text-gray-400 italic mt-1">No transcript available</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'photos' && (
                  <>
                    {photos.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-8">No photos found for this client</p>
                    ) : (
                      <div className="p-3">
                        <p className="text-xs text-gray-500 mb-2">{photos.length} photos</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {photos.map((photo) => (
                            <a key={photo.id} href={photo.image_url} target="_blank" rel="noopener noreferrer" className="group relative">
                              <img
                                src={photo.image_url}
                                alt="Service"
                                className="w-full h-32 object-cover rounded-lg border border-gray-200 group-hover:border-red-300 transition-colors"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent rounded-b-lg px-1.5 py-1">
                                <p className="text-[10px] text-white">
                                  {new Date(photo.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'transactions' && (
                  <>
                    {transactions.length === 0 ? (
                      <p className="text-xs text-gray-500 text-center py-8">No transactions found</p>
                    ) : (
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-gray-500">{transactions.length} transactions</p>
                          <p className="text-xs font-semibold text-gray-900">
                            Total: ${transactions.reduce((sum, t) => sum + parseFloat(t.amount || 0), 0).toFixed(2)}
                          </p>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                              <tr>
                                <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Date</th>
                                <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-500 uppercase">Amount</th>
                                <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-500 uppercase">Tip</th>
                                <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Source</th>
                                <th className="px-2 py-1 text-left text-[10px] font-medium text-gray-500 uppercase">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {transactions.map((txn) => (
                                <tr key={txn.id} className="hover:bg-gray-50">
                                  <td className="px-2 py-1.5 text-xs text-gray-700">
                                    {txn.date_time ? new Date(txn.date_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                  </td>
                                  <td className="px-2 py-1.5 text-xs text-gray-900 font-medium text-right">
                                    ${parseFloat(txn.amount || 0).toFixed(2)}
                                  </td>
                                  <td className="px-2 py-1.5 text-xs text-green-700 text-right">
                                    {parseFloat(txn.tip || 0) > 0 ? `$${parseFloat(txn.tip).toFixed(2)}` : '—'}
                                  </td>
                                  <td className="px-2 py-1.5 text-xs text-gray-500 capitalize">
                                    {txn.source || '—'}
                                  </td>
                                  <td className="px-2 py-1.5 text-xs">
                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                      txn.status === 'completed' ? 'bg-green-100 text-green-700' :
                                      txn.status === 'refunded' ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-600'
                                    }`}>
                                      {txn.status || '—'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}

              </div>
            </Panel>
          </div>
        )}

        {/* Call Modal */}
        <CallModal
          isOpen={showCallModal}
          onClose={() => setShowCallModal(false)}
          phoneNumber={client?.effective_phone || client?.person?.phone_number}
          personName={client?.effective_first_name ? `${client.effective_first_name} ${client.effective_last_name || ''}`.trim() : null}
          personId={client?.person?.id}
          apiService={apiService}
        />

        <SMSModal
          isOpen={showSMSModal}
          onClose={() => setShowSMSModal(false)}
          phoneNumber={client?.effective_phone || client?.person?.phone_number}
          personName={client?.effective_first_name ? `${client.effective_first_name} ${client.effective_last_name || ''}`.trim() : null}
          personId={client?.person?.id}
          apiService={apiService}
        />

        <AddCardModal
          isOpen={showAddCard}
          onClose={() => setShowAddCard(false)}
          clientId={client?.id}
          clientName={client?.effective_first_name ? `${client.effective_first_name} ${client.effective_last_name || ''}`.trim() : 'Client'}
          clientPhone={client?.effective_phone || client?.person?.phone_number}
          apiService={apiService}
          onCardSaved={() => { setShowAddCard(false); fetchClientDetails(); }}
        />
      </div>
    </div>
  );
}

export default ClientDetailPage;
